import { Box, Button, Stack, Theme, Typography } from '@mui/material'
import { FC, forwardRef, useCallback } from 'react'
import { IconButton, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { CommunityCommentInput } from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { Form, useForm, UseFormProps, useMessage, useTranslation } from '@/core'
import { CellFrame } from '@/core/architecture'

type TradeAcceptConfirmModalProps = {
  onClose: () => void
  onSubmit: (values: CommunityCommentInput) => Promise<void>
}

const schema: UseFormProps['schema'] = o => ({
  acceptComment: {
    artistId: o.string().optional().allow('', null),
    content: o.string().optional().allow('', null),
  },
})

type AcceptComment = {
  acceptComment: CommunityCommentInput
}

export const TradeAcceptConfirmModal: FC<TradeAcceptConfirmModalProps> = ({
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('myTradeDetail')
  const { showError } = useMessage()
  const {
    handleSubmit,
    register,
    formState: { isSubmitting },
  } = useForm<AcceptComment>({
    schema,
    defaultValues,
  })

  const handleAcceptConfirm = useCallback(
    async (values: AcceptComment) => {
      await onSubmit(values.acceptComment)
        .then(() => onClose())
        .catch(showError)
    },
    [onSubmit, onClose, showError]
  )

  return (
    <BaseLayout
      margin
      header={{
        left: <IconButton.Close edge="start" size="medium" onClick={onClose} />,
        title: t('trade'),
      }}
    >
      <Form onSubmit={handleSubmit(handleAcceptConfirm)}>
        <Stack direction="row" alignItems="center" sx={{ py: 3 }}>
          <Typography variant="subtitle1" color="text.secondary">
            {t('transactionMessage')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('any')}
          </Typography>
        </Stack>
        <Stack>
          <TextInputCell
            type="text"
            {...register('acceptComment.content')}
            textFieldProps={{
              sx: {
                px: 4,
                py: 3,
                mx: 0,
                mt: 0,
                mb: 3,
                minHeight: 124,
                bgcolor: 'grey.50',
                width: '100%',
                borderRadius: 1,
              },
              multiline: true,
              minRows: 1,
              maxRows: 5,
              variant: 'standard',
              helperText: '',
              inputProps: {
                sx: ({ typography }: Theme) => typography?.body2,
              },
              InputProps: {
                sx: { border: 'none' },
                disableUnderline: true,
              },
            }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {t('acceptConfirmNote')}
        </Typography>
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Button
            type="submit"
            loading={isSubmitting}
            size="large"
            sx={{ minWidth: 244 }}
          >
            {t('trade')}
          </Button>
        </Box>
      </Form>
    </BaseLayout>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('myTradeDetail')
    return (
      <CellFrame sx={{ width: '100%' }}>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const defaultValues: AcceptComment = {
  acceptComment: {
    artistId: '',
    content: '',
  },
}
